import {isString} from 'lodash';
import Token from 'markdown-it/lib/token';
import React, {FC, PropsWithChildren} from 'react';

import {MouseEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isCommonMarkGap} from '../../../../services/markdown';

/*
 * Props.
 */

interface MarkdownTokenFormatterProps {
  token: Token;
  childTokens?: ReadonlyArray<Token>;
  listDepth: number;
  onLinkClick?: (link: string, shiftKey?: boolean) => void;
}

/*
 * Component.
 */

// Note: this component is made in a way that it shouldn't be aware of where it's being used, so refrain from using custom styling here.

export const MarkdownTokenFormatter: FC<PropsWithChildren<MarkdownTokenFormatterProps>> = (props) => {
  const {token, childTokens, listDepth, children} = props;
  switch (token.type) {
    case 'softbreak':
      return <br />;

    case 'strong_open':
      return <strong>{children}</strong>;

    case 'em_open':
      return <em>{children}</em>;

    case 's_open':
      return <s>{children}</s>;

    case 'code_inline':
      return <code>{children}</code>;

    case 'blockquote_open':
      return <blockquote>{children}</blockquote>;

    case 'ordered_list_open':
      return (
        <ol style={orderedListStyle(listDepth)} start={parseStartAttribute(token.attrGet('start'))}>
          {children}
        </ol>
      );

    case 'bullet_list_open':
      return <ul style={unorderedListStyle(listDepth)}>{children}</ul>;

    case 'list_item_open':
      return <li style={listItemStyle(childTokens)}>{children}</li>;

    case 'paragraph_open':
      return <p>{children}</p>;

    case 'fence':
      return (
        <pre>
          <code>{children}</code>
        </pre>
      );

    case 'link_open':
      return renderLink(props);

    case 'image':
      return null;

    case 'html_inline':
    case 'html_block':
      // As an extra precaution, make sure the children were rendered to a string and not markup.
      if (!isString(children) || isCommonMarkGap(children)) {
        return null;
      }

      return <>{children}</>;

    default:
      return <>{children}</>;
  }
};

function parseStartAttribute(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue)) {
    return undefined;
  }

  return parsedValue;
}

function renderLink(props: PropsWithChildren<MarkdownTokenFormatterProps>) {
  const {token, onLinkClick, children} = props;
  const href = token.attrGet('href') || '#';
  const onLinkClickWrapper: MouseEventHandler = (e) => {
    if (!onLinkClick) {
      return;
    }

    e.preventDefault();
    onLinkClick(href, e.shiftKey);
  };

  return (
    <a href={href} onClick={onLinkClickWrapper} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function orderedListStyle(listDepth: number): React.CSSProperties {
  if (listDepth < 0) {
    throw new Error('List depth cannot be less than 0.');
  }

  const depth = listDepth % 3;

  switch (depth) {
    case 0:
      return {listStyleType: 'decimal'};
    case 1:
      return {listStyleType: 'lower-alpha'};
    case 2:
      return {listStyleType: 'lower-roman'};
    default:
      throw new Error('This should not happen.');
  }
}

function unorderedListStyle(listDepth: number): React.CSSProperties {
  if (listDepth < 0) {
    throw new Error('List depth cannot be less than 0.');
  }

  const depth = listDepth % 3;

  switch (depth) {
    case 0:
      return {listStyleType: 'disc'};
    case 1:
      return {listStyleType: 'circle'};
    case 2:
      return {listStyleType: 'square'};
    default:
      throw new Error('This should not happen.');
  }
}

function listItemStyle(childTokens: ReadonlyArray<Token> | undefined): React.CSSProperties {
  // If the first child token type is a list, hide the bullet/numeral. This
  // prevents multiple bullets/numerals on a single line for nested structures
  // like:
  // <ul>
  //   <li>
  //     <ul>
  //       <li>1a</li>
  //     </ul>
  //   </li>
  // </ul>
  const firstChildType = childTokens?.[0]?.type;

  if (firstChildType !== 'bullet_list_open' && firstChildType !== 'ordered_list_open') {
    return {};
  }

  return {
    listStyleType: 'none',
  };
}
